import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { OrderDto, OrderNotificationType, OrderStatus } from '@ecom/contracts';
import { useOrdersStore } from '@/stores/orders.store';
import { useAuthStore } from '@/stores/auth.store';
import { can } from '@/lib/session';
import { adminApi, errorMessage } from '@/lib/api-client';
import { mediaUrl } from '@/lib/media';
import { openFile, saveFile } from '@/lib/download';
import { telLink, whatsappLink } from '@/lib/contact';
import { useUiStore } from '@/i18n';
import { useT, useStatusLabel } from '@/i18n/use-t';
import type { MessageKey } from '@/i18n/messages';
import { count, date, dateTime, dayRange, humanize, money } from '@/lib/format';
import { canEditAddress, nextStatuses, progressOf, resendable, statusTone } from '@/lib/order-status';
import { paymentLabelKey, paymentTone } from '@/lib/payment-state';
import { Menu, type MenuItem } from '@/components/menu';
import { AsyncSelect } from '@/components/async-select';
import { staffOptions } from '@/lib/options';
import { AmountRow, DetailLayout, KeyValue, Page, Section, Thumb } from '@/components/page';
import { Alert, Button, CopyButton, Pill, Skeleton, cx, type PillTone } from '@/components/ui';
import { CheckIcon, ExternalIcon, KeyIcon, MailIcon, PencilIcon, PhoneIcon } from '@/components/icons';
import { StatusSheet } from './status-sheet';
import { OrderTimeline } from './timeline';
import { AddressSheet } from './address-sheet';
import { EditDetailsSheet, EditItemsSheet, detailsEditable, itemsEditable } from './edit-sheets';
import { RefundSheet, refundableLeft } from './refund-sheet';
import { itemCount } from './item-count';
import { pinLink } from '@ecom/contracts';

/**
 * One order: what was bought, where it goes, and what happens next.
 *
 * The header carries the next step and a way to any other legal move — each
 * with a note and a say in whether the customer is told (ADR-059). What was
 * bought, how far it has got, and what happened to it run down the wide
 * column; who it is for, where it goes and how it is paid sit beside it, with
 * the ways to reach the customer and to correct the address before it leaves.
 * Under "more": the order's details and — while no money has moved — its items
 * changed (ADR-062), and money given back without a return (ADR-061).
 */
/** Statuses at which a parcel will travel, as the label route holds them (ADR-068). */
const LABELLED: string[] = ['AWAITING_COLLECTION', 'PAID', 'PROCESSING', 'SHIPPED'];

function labelPrintable(order: OrderDto): boolean {
  return !order.isPickup && LABELLED.includes(order.status);
}

/** Statuses at which the supply has happened and an invoice exists. */
const INVOICEABLE: string[] = ['PAID', 'AWAITING_COLLECTION', 'PROCESSING', 'SHIPPED', 'READY_FOR_PICKUP', 'DELIVERED'];

const RETURN_TONES: Record<string, PillTone> = {
  REQUESTED: 'warning',
  APPROVED: 'info',
  RECEIVED: 'info',
  REFUNDED: 'success',
  REJECTED: 'danger',
  CANCELLED: 'neutral',
};

/** How the order is paid, in words: at the door, outside the store, or by card. */
function methodKey(method: OrderDto['paymentMethod']): MessageKey {
  return method === 'COD' ? 'paymentCod' : method === 'MANUAL' ? 'payManual' : method === 'WALLET' ? 'payWallet' : 'paymentCard';
}

export function OrderDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { current, currentLoading, currentError, updating, open, resend, clearError } = useOrdersStore();
  const user = useAuthStore((state) => state.user);
  const role = user?.role;
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const statusLabel = useStatusLabel();

  const [proposed, setProposed] = useState<OrderStatus | null>(null);
  const [editingAddress, setEditingAddress] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [editingItems, setEditingItems] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [labelError, setLabelError] = useState<string | null>(null);

  useEffect(() => {
    if (id) void open(id);
  }, [id, open, locale]);

  const back = { to: '/orders', label: t('orders') };

  if (currentLoading && !current) {
    return (
      <Page title={t('orders')} back={back} width="wide">
        <DetailLayout
          main={<Skeleton className="h-96" />}
          aside={
            <>
              <Skeleton className="h-40" />
              <Skeleton className="h-40" />
            </>
          }
        />
      </Page>
    );
  }

  if (!current || current.id !== id) {
    return (
      <Page title={t('orders')} back={back} width="wide">
        {currentError ? <Alert>{currentError}</Alert> : <Skeleton className="h-96" />}
      </Page>
    );
  }

  const order = current;
  const moves = nextStatuses(order.status, order.isPickup);
  // The move forward the header offers outright; cancelling waits in the panel and the menu.
  const forward = moves.find((move) => move !== 'CANCELLED') ?? null;
  const sheetOpen = proposed !== null || editingAddress || editingDetails || editingItems || refunding;
  const canRefund = refundableLeft(order, role) !== null;

  /** A sheet opening starts clean, not with the last one's refusal. */
  const opening = (show: (value: boolean) => void) => () => {
    clearError();
    show(true);
  };

  async function resendMessage(type: OrderNotificationType) {
    setNotice(null);
    if (await resend(order.id, type)) setNotice(t('resendQueued'));
  }

  const more: MenuItem[] = [
    // Changing the order (ADR-062): its details while it is open, its items while no money has moved.
    ...(detailsEditable(order) ? [{ label: t('editDetails'), onSelect: opening(setEditingDetails) }] : []),
    ...(itemsEditable(order) ? [{ label: t('editItems'), onSelect: opening(setEditingItems) }] : []),
    {
      label: t('printPackingSlip'),
      onSelect: () => navigate(`/orders/print?ids=${order.id}`),
    },
    // Where the parcel goes, and the cash its driver collects (ADR-068).
    ...(labelPrintable(order) && can(user, 'orders:fulfill')
      ? [
          {
            label: t('printShippingLabel'),
            onSelect: () => {
              setLabelError(null);
              openFile(() => adminApi.orders.label(order.id), `label-${order.number}.pdf`).catch((error: unknown) =>
                setLabelError(errorMessage(error, 'Could not print the label.')),
              );
            },
          },
        ]
      : []),
    ...resendable(order).map((type) => ({
      label: t('resendNotification', { name: t(`notify_${type}` as MessageKey) }),
      icon: <MailIcon className="size-4 text-ink-muted" />,
      disabled: updating,
      onSelect: () => void resendMessage(type),
    })),
    // Money back without a return — the owner's alone (ADR-061).
    ...(canRefund ? [{ label: t('refundOrder'), danger: true, onSelect: opening(setRefunding) }] : []),
    // An order is never erased: cancelling is how one goes away (ADR-062).
    ...(moves.includes('CANCELLED')
      ? [{ label: t('cancelOrder'), danger: true, onSelect: () => setProposed('CANCELLED') }]
      : []),
  ];

  return (
    <Page
      title={
        <span data-numeric dir="ltr">
          {order.number}
        </span>
      }
      subtitle={dateTime(order.placedAt ?? order.createdAt, locale)}
      back={back}
      meta={
        <>
          <Pill tone={statusTone(order.status)}>{statusLabel(order.status, order.isPickup)}</Pill>
          <Pill tone={order.paymentMethod === 'COD' ? 'warning' : 'neutral'}>{t(methodKey(order.paymentMethod))}</Pill>
        </>
      }
      actions={
        <>
          <Menu label={t('moreActions')} items={more} bordered />
          {/* Issued on first request, so it is available as soon as the order
              is payable — no separate "generate" step to forget. Fetched rather
              than linked: the API takes a bearer token, which a plain link
              cannot carry. */}
          {INVOICEABLE.includes(order.status) ? (
            <Button
              variant="secondary"
              disabled={downloading}
              onClick={async () => {
                setDownloading(true);
                try {
                  await saveFile(() => adminApi.invoices.forOrder(order.id), `${order.number}.pdf`);
                  // The first download issues it: read the order again, so the
                  // document is listed and the items show as settled (ADR-062).
                  if (!order.documents?.length) void open(order.id);
                } finally {
                  setDownloading(false);
                }
              }}
            >
              {downloading ? t('loading') : t('downloadInvoice')}
            </Button>
          ) : null}
          {moves.length > 0 ? (
            <Button variant="secondary" onClick={() => setProposed(forward ?? moves[0] ?? null)} disabled={sheetOpen}>
              {t('changeStatus')}
            </Button>
          ) : null}
          {forward ? (
            // A dialog is modal: leaving this focusable behind it puts two
            // controls with the same name on screen at once.
            <Button onClick={() => setProposed(forward)} disabled={sheetOpen}>
              {t('markAs')} {statusLabel(forward)}
            </Button>
          ) : (
            <span className="text-sm text-ink-muted">{t('nothingFurther')}</span>
          )}
        </>
      }
      width="wide"
    >
      {notice ? <Alert tone="success">{notice}</Alert> : null}
      {labelError ? <Alert>{labelError}</Alert> : null}
      {currentError && !sheetOpen ? <Alert>{currentError}</Alert> : null}

      <DetailLayout
        main={
          <>
            <Progress order={order} />
            <Items order={order} />
            <Returns order={order} />
            <Documents order={order} />
            <OrderTimeline order={order} />
          </>
        }
        aside={
          <>
            {order.customerNote ? (
              // What the shopper wrote — a gift message, a note for the tailor
              // (R5). First, because the person packing must not miss it.
              <Section title={t('customerNote')} className="border-warning/40 bg-warning/5">
                <p className="whitespace-pre-line text-sm text-ink">{order.customerNote}</p>
              </Section>
            ) : null}

            <AssigneeCard order={order} />
            <CustomerCard order={order} />
            <SourceCard order={order} />

            {!order.requiresShipping ? (
              // Nothing ships (ADR-077): who it is for, and how it arrived.
              <Section title={t('digitalNoShipping')}>
                <p className="text-sm text-ink">{order.shippingAddress.fullName}</p>
                <p dir="ltr" data-numeric className="text-end text-sm text-ink-muted rtl:text-start">
                  {order.shippingAddress.phone}
                </p>
                <p className="mt-2 text-xs text-ink-muted">{t('digitalNoShippingHint')}</p>
              </Section>
            ) : (
            <Section
              title={t('orderShippingAddress')}
              action={
                <>
                  <CopyButton value={addressText(order)} label={t('copy')} copiedLabel={t('copied')} />
                  {canEditAddress(order.status) ? (
                    <Button variant="ghost" size="sm" onClick={() => setEditingAddress(true)} disabled={sheetOpen}>
                      <PencilIcon className="size-3.5" />
                      {t('editAddress')}
                    </Button>
                  ) : null}
                </>
              }
            >
              <address className="text-sm not-italic leading-relaxed text-ink-muted">
                <span className="text-ink">{order.shippingAddress.fullName}</span>
                <br />
                {order.shippingAddress.line1}
                {order.shippingAddress.line2 ? <>, {order.shippingAddress.line2}</> : null}
                {order.shippingAddress.district ? <>, {order.shippingAddress.district}</> : null}
                <br />
                {order.shippingAddress.city}, {order.shippingAddress.region}
                {order.shippingAddress.shortCode ? (
                  // The code couriers ask for (R5): Latin, left to right, copyable.
                  <>
                    {' · '}
                    <span dir="ltr" data-numeric className="font-medium text-ink">
                      {order.shippingAddress.shortCode}
                    </span>
                  </>
                ) : null}
                <br />
                <span dir="ltr" data-numeric>
                  {order.shippingAddress.phone}
                </span>
                {order.shippingAddress.directions ? (
                  <>
                    <br />
                    {order.shippingAddress.directions}
                  </>
                ) : null}
                {/* The pin the shopper dropped (ADR-093): a map a person opens, coordinates to copy. */}
                {order.shippingAddress.latitude !== null && order.shippingAddress.longitude !== null ? (
                  <>
                    <br />
                    <a
                      href={pinLink({ latitude: order.shippingAddress.latitude, longitude: order.shippingAddress.longitude })}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-700 hover:underline"
                    >
                      {t('orderPinOnMap')}
                    </a>{' '}
                    <span dir="ltr" data-numeric className="text-xs text-ink-muted">
                      ({order.shippingAddress.latitude.toFixed(6)}, {order.shippingAddress.longitude.toFixed(6)})
                    </span>
                  </>
                ) : null}
              </address>
            </Section>
            )}

            {order.shippingMethodName || order.deliveryEstimate || order.isPickup || order.shipments.length > 0 ? (
              // How it leaves, the dates promised at checkout (R6), and the parcel once it has.
              <Section title={t('delivery')}>
                <div className="flex flex-wrap items-center gap-2">
                  {order.shippingMethodName ? <p className="text-sm text-ink">{order.shippingMethodName}</p> : null}
                  {order.isPickup ? <Pill tone="info">{t('pickupAtStore')}</Pill> : null}
                </div>
                {order.deliveryEstimate ? (
                  <p className="mt-1 text-sm text-ink-muted">
                    {t('promisedDates')}: {dayRange(order.deliveryEstimate.from, order.deliveryEstimate.to, locale)}
                  </p>
                ) : null}
                {order.pickupInstructions ? (
                  <p className="mt-2 whitespace-pre-line text-xs text-ink-muted">{order.pickupInstructions[locale]}</p>
                ) : null}
                <ShipsWith order={order} />
                {order.shipments.map((shipment, index) => (
                  <div key={index} className="mt-3 flex flex-col gap-1 border-t border-line pt-3 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-ink">{shipment.carrier}</span>
                      <Pill tone={shipment.status === 'DELIVERED' ? 'success' : shipment.status === 'FAILED' ? 'danger' : 'neutral'}>
                        {t(`shipmentStatus_${shipment.status}` as MessageKey)}
                      </Pill>
                    </div>
                    {shipment.trackingNumber ? (
                      <div className="flex items-center justify-between gap-2">
                        <span dir="ltr" data-numeric className="text-ink-muted">
                          {shipment.trackingNumber}
                        </span>
                        {shipment.trackingUrl ? (
                          <a
                            href={shipment.trackingUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:underline"
                          >
                            {t('trackShipment')}
                            <ExternalIcon className="size-3.5" />
                          </a>
                        ) : (
                          <CopyButton value={shipment.trackingNumber} label={t('copy')} copiedLabel={t('copied')} />
                        )}
                      </div>
                    ) : null}
                  </div>
                ))}
              </Section>
            ) : null}

            <PaymentCard order={order} />

            {order.billing ? (
              // Who the invoice is addressed to, when it is not the recipient (R5).
              <Section title={t('billedTo')}>
                <p className="text-sm text-ink">{order.billing.companyName ?? order.billing.name}</p>
                {order.billing.companyName ? <p className="text-sm text-ink-muted">{order.billing.name}</p> : null}
                {order.billing.line1 || order.billing.city ? (
                  <p className="text-sm text-ink-muted">
                    {[order.billing.line1, order.billing.city].filter(Boolean).join(', ')}
                  </p>
                ) : null}
                {order.billing.vatNumber ? (
                  <p className="mt-1 text-xs text-ink-muted">
                    {t('vatNumber')}:{' '}
                    <span dir="ltr" data-numeric className="text-ink">
                      {order.billing.vatNumber}
                    </span>
                  </p>
                ) : null}
              </Section>
            ) : null}
          </>
        }
      />

      <StatusSheet order={order} initial={proposed} onClose={() => setProposed(null)} />
      <AddressSheet order={order} open={editingAddress} onClose={() => setEditingAddress(false)} />
      <EditDetailsSheet order={order} open={editingDetails} onClose={() => setEditingDetails(false)} />
      <EditItemsSheet order={order} open={editingItems} onClose={() => setEditingItems(false)} />
      <RefundSheet order={order} open={refunding} onClose={() => setRefunding(false)} />
    </Page>
  );
}

/** How far along the order is — placed, paid, prepared, sent, delivered — or nothing for one that left the road. */
function Progress({ order }: { order: OrderDto }) {
  const t = useT();
  const steps = progressOf(order);
  if (!steps) return null;

  const wordFor = (id: string): MessageKey => {
    if (id === 'paid' && order.paymentMethod === 'COD') return 'progress_paid_cod';
    if (id === 'sent' && order.isPickup) return 'progress_sent_pickup';
    if (id === 'delivered' && order.isPickup) return 'progress_delivered_pickup';
    return `progress_${id}` as MessageKey;
  };

  return (
    <ol
      className={cx(
        'grid rounded-card border border-line bg-surface px-2 py-5 shadow-card sm:px-4',
        steps.length === 3 ? 'grid-cols-3' : 'grid-cols-5',
      )}
    >
      {steps.map((step, index) => (
        <li key={step.id} className="relative flex min-w-0 flex-col items-center gap-2 px-1 text-center">
          {/* The track from the step before, emerald as far as the order has come. */}
          {index > 0 ? (
            <span
              aria-hidden
              className={cx(
                'absolute top-3.5 end-[calc(50%+1.125rem)] start-[calc(-50%+1.125rem)] h-0.5 rounded-full',
                step.state === 'todo' ? 'bg-line' : 'bg-brand-500',
              )}
            />
          ) : null}
          <span
            aria-hidden
            className={cx(
              'relative inline-flex size-7 items-center justify-center rounded-full border-2 text-xs',
              step.state === 'done'
                ? 'border-brand-600 bg-brand-600 text-brand-ink'
                : step.state === 'current'
                  ? 'border-brand-500 bg-surface text-brand-600 ring-4 ring-brand-500/15'
                  : 'border-line bg-surface text-ink-faint',
            )}
          >
            {step.state === 'done' ? (
              <CheckIcon className="size-3.5" />
            ) : step.state === 'current' ? (
              <span className="size-2 rounded-full bg-brand-500" />
            ) : null}
          </span>
          <span
            className={cx(
              'line-clamp-2 text-xs leading-snug',
              step.state === 'todo' ? 'text-ink-faint' : step.state === 'current' ? 'font-semibold text-ink' : 'text-ink-muted',
            )}
          >
            {t(wordFor(step.id))}
          </span>
        </li>
      ))}
    </ol>
  );
}

/** What was bought, as it was bought, and what it came to. */
function Items({ order }: { order: OrderDto }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { deliverDigital, updating } = useOrdersStore();
  const [delivered, setDelivered] = useState(false);
  const currency = order.totals.currency;
  const units = order.items.reduce((sum, item) => sum + item.quantity, 0);
  const refunded = (order.payments ?? []).reduce((sum, payment) => sum + payment.refundedAmount, 0);
  // Files and codes go out once it is paid, and again on request (ADR-077).
  const digital = order.items.some((item) => item.digital) && order.paidAt !== null && !['CANCELLED', 'REFUNDED'].includes(order.status);

  return (
    <Section
      title={t('items')}
      subtitle={delivered ? t('digitalSentAgain') : itemCount(units, t, locale)}
      flush
      action={
        digital ? (
          <Button
            variant="secondary"
            size="sm"
            disabled={updating}
            onClick={async () => setDelivered(await deliverDigital(order.id))}
            title={t('deliverNowHint')}
          >
            <KeyIcon className="size-3.5" />
            {t('deliverNow')}
          </Button>
        ) : null
      }
    >
      {/* Rows rather than a table: on a phone the name needs the width, and
          the amounts sit under it instead of squeezing it to a column. */}
      <ul className="border-t border-line">
        {order.items.map((item) => (
          <li key={item.id} className="flex items-start gap-3 border-b border-line px-5 py-3.5 last:border-0">
            {/* The picture as it was at purchase — what the customer is
                looking at when they call about it. */}
            <Thumb src={mediaUrl(item.imageUrl)} size="sm" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <span className="min-w-0 text-sm text-ink">{item.name}</span>
                <span data-numeric className="whitespace-nowrap text-sm font-medium text-ink">
                  {money(item.lineTotal, locale, currency)}
                </span>
              </div>
              <div className="mt-0.5 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-xs text-ink-muted">
                <span className="min-w-0 break-words">
                  <span data-numeric dir="ltr">
                    {item.sku}
                  </span>
                  {Object.entries(item.optionLabels).length > 0
                    ? ` · ${Object.entries(item.optionLabels)
                        .map(([name, value]) => `${name}: ${value}`)
                        .join(', ')}`
                    : ''}
                </span>
                <span data-numeric className="whitespace-nowrap">
                  {count(item.quantity, locale)} × {money(item.unitPrice, locale, currency)}
                </span>
              </div>
              {item.paidTotal < item.lineTotal ? (
                // What the line was really paid once its share of the offers and
                // coupon is taken off — what a return of it is worth (ADR-061).
                <p data-numeric className="mt-0.5 text-end text-xs text-ink-muted">
                  {t('linePaid', { amount: money(item.paidTotal, locale, currency) })}
                </p>
              ) : null}
              {item.fields.length > 0 ? (
                // What the customer entered for this line (S11) — the
                // measurement, the name to embroider.
                <dl className="mt-1.5 flex flex-col gap-0.5 rounded-control bg-surface-sunken px-2.5 py-1.5 text-xs">
                  {item.fields.map((field) => (
                    <div key={field.code} className="flex gap-1.5">
                      <dt className="text-ink-muted">{field.label}:</dt>
                      <dd className="font-medium text-ink">{field.display}</dd>
                    </div>
                  ))}
                </dl>
              ) : null}
              {item.digital ? <DigitalLine digital={item.digital} /> : null}
              {item.preorderShipsOn ? (
                // Sold beyond the shelf: it ships when the stock does (ADR-079).
                <p className="mt-1.5">
                  <Pill tone="info">{t('preorderLine', { date: date(item.preorderShipsOn, locale) })}</Pill>
                </p>
              ) : null}
            </div>
          </li>
        ))}
      </ul>

      <dl className="flex flex-col gap-2 border-t border-line p-5 text-sm">
        <AmountRow label={t('subtotal')} value={money(order.totals.subtotal, locale, currency)} />
        {order.totals.promotionTotal > 0 ? (
          <AmountRow label={t('promotionSavings')} value={`−${money(order.totals.promotionTotal, locale, currency)}`} />
        ) : null}
        {order.totals.discountTotal > 0 ? (
          <AmountRow
            label={order.discountCode ? `${t('discount')} (${order.discountCode})` : t('discount')}
            value={`−${money(order.totals.discountTotal, locale, currency)}`}
          />
        ) : null}
        <AmountRow label={t('shipping')} value={money(order.totals.shippingTotal, locale, currency)} />
        {order.totals.paymentFee > 0 ? (
          <AmountRow label={t('paymentFee')} value={money(order.totals.paymentFee, locale, currency)} />
        ) : null}
        <AmountRow label={t('total')} value={money(order.totals.grandTotal, locale, currency)} emphasis />
        {refunded > 0 ? <AmountRow label={t('paymentRefunded')} value={`−${money(refunded, locale, currency)}`} /> : null}
        {/* VAT is contained in the total, never added to it (ADR-017). */}
        <p className="text-xs text-ink-muted">
          {t('vatIncluded', { amount: money(order.totals.taxTotal, locale, currency) })}
        </p>
      </dl>
    </Section>
  );
}

/** What a file or code line handed over (ADR-077): the codes, and each file's downloads. */
function DigitalLine({ digital }: { digital: NonNullable<OrderDto['items'][number]['digital']> }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  return (
    <div className="mt-1.5 flex flex-col gap-1.5 rounded-control bg-surface-sunken px-2.5 py-2 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        {digital.deliveredAt ? (
          <Pill tone="success">{t('digitalHandedOver', { when: dateTime(digital.deliveredAt, locale) })}</Pill>
        ) : (
          <Pill tone="warning">{t('digitalWaiting')}</Pill>
        )}
        {digital.availableUntil ? <span className="text-ink-muted">{t('digitalLinksUntil', { date: dateTime(digital.availableUntil, locale) })}</span> : null}
      </div>
      {digital.codes.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {digital.codes.map((code) => (
            <code key={code} dir="ltr" className="rounded border border-line bg-surface px-1.5 py-0.5 font-mono text-ink">
              {code}
            </code>
          ))}
        </div>
      ) : null}
      {digital.files.map((file) => (
        <div key={file.id} className="flex flex-wrap items-baseline justify-between gap-x-3">
          <bdi className="min-w-0 truncate text-ink">{file.name}</bdi>
          <span className="text-ink-muted" data-numeric>
            {t('digitalDownloads', { used: count(file.downloads, locale), total: count(file.downloads + file.downloadsLeft, locale) })}
          </span>
        </div>
      ))}
    </div>
  );
}

/** The returns raised against the order, each linking to its own page. */
function Returns({ order }: { order: OrderDto }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  if (!order.returns || order.returns.length === 0) return null;

  return (
    <Section title={t('orderReturns')} flush>
      <ul className="flex flex-col">
        {order.returns.map((row) => (
          <li key={row.id} className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-5 py-3 text-sm">
            <div className="min-w-0">
              <Link
                to={`/returns?search=${encodeURIComponent(row.number)}`}
                dir="ltr"
                className="font-medium text-brand-700 hover:underline"
              >
                {row.number}
              </Link>
              <span className="block text-xs text-ink-muted">
                {count(row.quantity, locale)} × {row.productName}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Pill tone={RETURN_TONES[row.status] ?? 'neutral'}>{t(`returnStatus${row.status}` as MessageKey)}</Pill>
              <span data-numeric className="text-ink">
                {money(row.refundedAmount > 0 ? row.refundedAmount : row.amount, locale, order.totals.currency)}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </Section>
  );
}

/**
 * Its invoice and any credit notes (ADR-067) — each as issued, opened from
 * here. A credit note reads negative: it is money the invoice gave back.
 */
function Documents({ order }: { order: OrderDto }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const [opening, setOpening] = useState<string | null>(null);
  if (!order.documents || order.documents.length === 0) return null;

  return (
    <Section title={t('orderDocuments')} flush>
      <ul className="flex flex-col">
        {order.documents.map((document) => {
          const credit = document.kind === 'CREDIT_NOTE';
          return (
            <li key={document.id} className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-5 py-3 text-sm">
              <div className="flex min-w-0 items-center gap-2">
                <button
                  type="button"
                  dir="ltr"
                  disabled={opening !== null}
                  className="font-medium text-brand-700 hover:underline disabled:opacity-60"
                  title={t('openDocument')}
                  onClick={async () => {
                    setOpening(document.id);
                    try {
                      await saveFile(() => adminApi.invoices.document(document.id), `${document.number}.pdf`);
                    } finally {
                      setOpening(null);
                    }
                  }}
                >
                  {document.number}
                </button>
                <Pill tone={credit ? 'warning' : 'neutral'}>{credit ? t('creditNote') : t('invoice')}</Pill>
                {opening === document.id ? <span className="text-xs text-ink-muted">{t('loading')}</span> : null}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-ink-muted">{dateTime(document.issuedAt, locale)}</span>
                <span data-numeric className="font-medium text-ink">
                  {money(credit ? -document.total : document.total, locale, document.currency)}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

/**
 * Who has it (ADR-074): someone on the staff, or nobody — "Assign to me" in
 * one press, anyone else from a searched list. Changes nothing else.
 */
function AssigneeCard({ order }: { order: OrderDto }) {
  const t = useT();
  const { assign, updating } = useOrdersStore();
  const me = useAuthStore((state) => state.user);
  const mayAssign = can(me, 'orders:fulfill');
  const mine = order.assignee?.id === me?.id;

  return (
    <Section title={t('assignedTo')}>
      {mayAssign ? (
        <div className="flex flex-col gap-2">
          <AsyncSelect
            id="order-assignee"
            value={order.assignee?.id ?? ''}
            emptyLabel={t('assignedToNobody')}
            fetchPage={staffOptions.page}
            fetchOne={staffOptions.one}
            onChange={(assigneeId) => void assign(order.id, assigneeId || null)}
          />
          {me && !mine ? (
            <Button variant="secondary" size="sm" className="self-start" disabled={updating} onClick={() => void assign(order.id, me.id)}>
              {t('assignToMe')}
            </Button>
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-ink">{order.assignee?.name ?? t('assignedToNobody')}</p>
      )}
    </Section>
  );
}

/**
 * Where it came from (ADR-097): the channel, and what the visit's link said —
 * the campaign, the site that sent it, the page it landed on. Nothing for an
 * order placed before sources were kept.
 */
function SourceCard({ order }: { order: OrderDto }) {
  const t = useT();
  const source = order.attribution;
  const conversions = order.conversions ?? [];
  if (!source?.channel && conversions.length === 0) return null;
  const tags = [source?.source, source?.medium].filter(Boolean).join(' / ');

  return (
    <Section title={t('orderSource')}>
      <dl className="flex flex-col gap-3">
        {source?.channel ? <KeyValue label={t('sourceChannel')}>{t(`channel_${source.channel}`)}</KeyValue> : null}
        {source?.campaign ? <KeyValue label={t('sourceCampaign')}>{source.campaign}</KeyValue> : null}
        {tags ? (
          <KeyValue label={t('sourceTags')}>
            <span dir="ltr">{tags}</span>
          </KeyValue>
        ) : null}
        {source?.referrer ? (
          <KeyValue label={t('sourceReferrer')}>
            <span dir="ltr">{source.referrer}</span>
          </KeyValue>
        ) : null}
        {source?.landing ? (
          <KeyValue label={t('sourceLanding')}>
            <span dir="ltr" className="break-all">
              {source.landing}
            </span>
          </KeyValue>
        ) : null}
        {source?.clickId ? <KeyValue label={t('sourceClickId')}>{t(`clickId_${source.clickId}`)}</KeyValue> : null}
        {conversions.length > 0 ? (
          // Where the purchase was reported from the server (ADR-100).
          <KeyValue label={t('conversionsReportedTo')}>
            <span className="flex flex-wrap gap-1.5">
              {conversions.map((entry) => (
                <span key={entry.platform} title={entry.error ?? undefined}>
                  <Pill tone={entry.status === 'SENT' ? 'success' : 'danger'}>{t(`conversionPlatform_${entry.platform}`)}</Pill>
                </span>
              ))}
            </span>
          </KeyValue>
        ) : null}
      </dl>
    </Section>
  );
}

/** Who placed it, what they are to the store, and the ways to reach them. */
function CustomerCard({ order }: { order: OrderDto }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const customer = order.customer;
  const phone = order.phone ?? order.shippingAddress.phone;
  const whatsapp = whatsappLink(phone);
  const call = telLink(phone);

  return (
    <Section
      title={t('customer')}
      action={
        customer?.id ? (
          <Link to={`/customers/${customer.id}`} className="text-xs font-medium text-brand-700 hover:underline">
            {t('viewCustomer')}
          </Link>
        ) : (
          <Pill>{t('guestCustomer')}</Pill>
        )
      }
    >
      <p className="text-sm font-medium text-ink">{customer?.name ?? order.shippingAddress.fullName}</p>
      {customer && customer.tags.length > 0 ? (
        // The store's words for them — "refused at the door" is read where the order is packed (ADR-064).
        <div className="mt-1 flex flex-wrap gap-1">
          {customer.tags.map((tag) => (
            <Pill key={tag} tone="info">
              {tag}
            </Pill>
          ))}
        </div>
      ) : null}
      {customer ? (
        <p className="mt-0.5 text-xs text-ink-muted">
          {customer.orderCount <= 1 ? (
            <Pill tone="info">{t('customerFirstOrder')}</Pill>
          ) : (
            <>
              {t('customerOrderCount', { count: count(customer.orderCount, locale) })} ·{' '}
              {t('customerSpent', { amount: money(customer.totalSpent, locale, order.totals.currency) })}
            </>
          )}
        </p>
      ) : null}

      <dl className="mt-3 flex flex-col gap-1.5 text-sm">
        <div className="flex items-center justify-between gap-2">
          <dd className="min-w-0 truncate text-ink-muted" dir="ltr">
            {order.email}
          </dd>
          <CopyButton value={order.email} label={t('copy')} copiedLabel={t('copied')} />
        </div>
        {phone ? (
          <div className="flex items-center justify-between gap-2">
            <dd className="text-ink-muted" dir="ltr" data-numeric>
              {phone}
            </dd>
            <CopyButton value={phone} label={t('copy')} copiedLabel={t('copied')} />
          </div>
        ) : null}
      </dl>

      <div className="mt-3 grid grid-cols-3 gap-2">
        {whatsapp ? (
          <a
            href={whatsapp}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-1.5 rounded-control border border-line px-2 py-2 text-xs font-medium text-success hover:bg-surface-sunken"
          >
            <ChatGlyph />
            {t('contactWhatsapp')}
          </a>
        ) : null}
        {call ? (
          <a
            href={call}
            className="inline-flex items-center justify-center gap-1.5 rounded-control border border-line px-2 py-2 text-xs font-medium text-ink hover:bg-surface-sunken"
          >
            <PhoneIcon className="size-3.5" />
            {t('contactCall')}
          </a>
        ) : null}
        <a
          href={`mailto:${order.email}?subject=${encodeURIComponent(order.number)}`}
          className="inline-flex items-center justify-center gap-1.5 rounded-control border border-line px-2 py-2 text-xs font-medium text-ink hover:bg-surface-sunken"
        >
          <MailIcon className="size-3.5" />
          {t('contactEmail')}
        </a>
      </div>
    </Section>
  );
}

/** How it was paid, each attempt with the provider's reference, and what went back. */
function PaymentCard({ order }: { order: OrderDto }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const payments = order.payments ?? [];

  return (
    <Section title={t('orderPayment')}>
      <dl className="flex flex-col gap-3">
        <KeyValue label={t('paymentMethod')}>{t(methodKey(order.paymentMethod))}</KeyValue>
        {payments.map((payment, index) => (
          <div key={index} className="flex flex-col gap-1.5 rounded-control border border-line p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm text-ink">{payment.provider === 'wallet' ? t('payWallet') : humanize(payment.provider)}</span>
              {/* Cash reads in its own words: "not collected yet", not "pending" (ADR-061). */}
              <Pill tone={paymentTone(payment.status)}>{t(paymentLabelKey(payment.status, payment.provider === 'cod'))}</Pill>
            </div>
            {payment.provider === 'cod' && payment.status === 'PENDING' ? (
              <p className="text-xs text-ink-muted">{t('codCollectsOnDelivery')}</p>
            ) : null}
            <div className="flex items-center justify-between gap-2 text-sm">
              <span className="text-ink-muted">{t('total')}</span>
              <span data-numeric className="font-medium text-ink">
                {money(payment.amount, locale, payment.currency)}
              </span>
            </div>
            {payment.refundedAmount > 0 ? (
              <div className="flex items-center justify-between gap-2 text-sm">
                <span className="text-ink-muted">{t('paymentRefunded')}</span>
                <span data-numeric className="text-danger">
                  −{money(payment.refundedAmount, locale, payment.currency)}
                </span>
              </div>
            ) : null}
            {payment.paymentUrl ? (
              // A payment-link order still waiting: the page to send the customer (ADR-062).
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="text-ink-muted">{t('paymentLink')}</span>
                <span className="flex min-w-0 items-center gap-1">
                  <span dir="ltr" className="truncate text-ink">
                    {payment.paymentUrl}
                  </span>
                  <CopyButton value={payment.paymentUrl} label={t('copyLink')} copiedLabel={t('copied')} />
                </span>
              </div>
            ) : null}
            {payment.outside ? (
              // Paid outside the store: how, and the reference to find it by (ADR-062).
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="text-ink-muted">{t('receivedBy')}</span>
                <span className="text-end text-ink">
                  {t(`refundMethod${payment.outside.method}` as MessageKey)}
                  {payment.outside.reference ? (
                    <span dir="ltr" data-numeric className="block text-ink-muted">
                      {payment.outside.reference}
                    </span>
                  ) : null}
                </span>
              </div>
            ) : null}
            {payment.providerPaymentId ? (
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="text-ink-muted">{t('paymentReference')}</span>
                <span className="flex items-center gap-1">
                  <span dir="ltr" data-numeric className="truncate text-ink">
                    {payment.providerPaymentId}
                  </span>
                  <CopyButton value={payment.providerPaymentId} label={t('copy')} copiedLabel={t('copied')} />
                </span>
              </div>
            ) : null}
          </div>
        ))}
        {order.priceList ? (
          // Why its prices are not the shop's: a trade customer's list priced it (ADR-045).
          <KeyValue label={t('priceList')}>
            {order.priceList.name}
            <span className="mt-0.5 block text-xs text-ink-muted">{t('orderPricedFromList')}</span>
          </KeyValue>
        ) : null}
      </dl>
    </Section>
  );
}

/** The address as a courier's form wants it pasted. */
function addressText(order: OrderDto): string {
  const address = order.shippingAddress;
  return [
    address.fullName,
    address.phone,
    [address.line1, address.line2, address.district].filter(Boolean).join(', '),
    [address.city, address.region, address.postalCode].filter(Boolean).join(', '),
    address.shortCode,
    address.directions,
  ]
    .filter(Boolean)
    .join('\n');
}

function ChatGlyph() {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className="size-3.5" fill="currentColor">
      <path d="M12 2a10 10 0 0 0-8.66 15l-1.3 4.74 4.86-1.27A10 10 0 1 0 12 2Zm0 18.2a8.2 8.2 0 0 1-4.18-1.15l-.3-.18-2.88.75.77-2.8-.2-.3A8.2 8.2 0 1 1 12 20.2Zm4.5-6.14c-.25-.12-1.46-.72-1.69-.8-.23-.08-.39-.12-.56.13-.16.24-.64.8-.79.96-.14.17-.29.19-.54.06-.25-.12-1.04-.38-1.98-1.22-.73-.65-1.23-1.46-1.37-1.7-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.43.12-.15.16-.25.25-.41.08-.17.04-.31-.02-.44-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.41-.56-.42h-.48a.92.92 0 0 0-.67.31c-.23.25-.88.86-.88 2.09s.9 2.43 1.03 2.6c.12.16 1.77 2.7 4.29 3.79.6.26 1.07.41 1.43.53.6.19 1.15.16 1.58.1.48-.07 1.46-.6 1.67-1.18.2-.58.2-1.07.14-1.18-.06-.1-.22-.16-.47-.29Z" />
    </svg>
  );
}

/**
 * Which shipping provider has the order, and the courier the shopper's
 * delivery method ships with — booked, or to be created there (ADR-127/128).
 * Read from the order's own record of sending it, so it says what was sent.
 */
function ShipsWith({ order }: { order: OrderDto }) {
  const t = useT();
  const sent = [...(order.events ?? [])].reverse().find((event) => event.type === 'courier.sent');
  if (!sent) return null;
  const data = (sent.data ?? {}) as Record<string, unknown>;
  const provider = typeof data.courier === 'string' ? data.courier.toUpperCase() : null;
  const service = typeof data.service === 'string' ? data.service : null;
  return (
    <p className="mt-2 text-sm text-ink">
      <span className="text-ink-muted">{t('shipWithOrder')}: </span>
      {[provider, service].filter(Boolean).join(' · ')}
      {data.booked === true ? <span className="ms-2"><Pill tone="success">{t('courierBooked')}</Pill></span> : null}
      {data.booked === false ? <span className="ms-2 text-xs text-ink-muted">({t('courierToBook')})</span> : null}
    </p>
  );
}
